import { useEventListener } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { SelectedIconPosition } from "#ce/components/SpList/props/list";

/**
 * Places a list row's active icon before or after its title, from `activeIconPosition`.
 *
 * @param {{ activeIconPosition: string, activePrependIcon: string|undefined }} props  The row's props.
 * @param {{ active: import("vue").Ref<boolean>, checkbox: import("vue").Ref<boolean>,
 *   hasPrependVisual: import("vue").Ref<boolean>, prependSlot: import("vue").Ref<HTMLSlotElement|null> }} options
 *   - active: the row shows its active state
 *   - checkbox: the row shows its selection in a checkbox, so it shows no trailing active icon
 *   - hasPrependVisual: the row's props give it a prepend visual
 *   - prependSlot: the row's `prepend` slot, whose assigned content (an element or non-blank text, through
 *     any forwarding slots) also counts as a prepend visual
 * @returns {{ iconAtEnd: import("vue").ComputedRef<boolean>, leadingActiveIcon: import("vue").ComputedRef<boolean>,
 *   trailingActiveIcon: import("vue").ComputedRef<string|undefined>, dropsPrepend: import("vue").ComputedRef<boolean>}}
 *   - iconAtEnd: the row places its active icon after the title
 *   - leadingActiveIcon: the active icon replaces the prepend icon
 *   - trailingActiveIcon: the icon name the append area shows, when there is one
 *   - dropsPrepend: the row places its active icon after the title and has nothing to prepend
 */
export function useActiveIconPosition(props, { active, checkbox, hasPrependVisual, prependSlot }) {
  const hasPrependSlotContent = ref(false);

  useEventListener(prependSlot, "slotchange", readPrependSlotContent);
  watch(prependSlot, readPrependSlotContent, { immediate: true, flush: "post" });

  const iconAtEnd = computed(() => props.activeIconPosition === SelectedIconPosition.END);
  const leadingActiveIcon = computed(() => active.value && !iconAtEnd.value);
  const trailingActiveIcon = computed(() =>
    active.value && iconAtEnd.value && !checkbox.value ? props.activePrependIcon : undefined,
  );
  const dropsPrepend = computed(() => iconAtEnd.value && !hasPrependVisual.value && !hasPrependSlotContent.value);

  function readPrependSlotContent() {
    hasPrependSlotContent.value = Boolean(prependSlot.value?.assignedNodes({ flatten: true }).some(isContent));
  }

  return { iconAtEnd, leadingActiveIcon, trailingActiveIcon, dropsPrepend };
}

function isContent(node) {
  return node.nodeType === Node.ELEMENT_NODE || Boolean(node.textContent.trim());
}
